/**
 * Cart submodals consolidados — frete, cupom, nota e presente.
 * Inclui compensação dinâmica da barra de preview do Shopify.
 * Empório Saibai · Build 2026-08-09.
 */

const SaibaiPreviewViewport = (() => {
  const selector = [
    '#preview-bar-iframe',
    'iframe[id*="preview-bar"]',
    'iframe[src*="preview_bar"]',
    'iframe[src*="preview-bar"]',
    '[data-shopify-preview-bar]'
  ].join(',');

  const subscribers = new Set();
  let mutationObserver = null;
  let resizeObserver = null;
  let frameId = 0;
  let started = false;
  let state = { offset: 0, viewportHeight: 0, availableHeight: 0 };

  const viewportHeight = () => Math.max(
    1,
    Math.round(window.innerHeight || document.documentElement.clientHeight || 1)
  );

  const candidates = () => Array.from(new Set(document.querySelectorAll(selector)));

  const isVisibleBottomBar = (element, height) => {
    if (!(element instanceof Element)) return false;

    const style = window.getComputedStyle(element);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      Number.parseFloat(style.opacity || '1') === 0
    ) return false;

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height < 24 || rect.height > 180) return false;
    if (rect.top >= height || rect.bottom < height - 8) return false;

    return true;
  };

  const detectOffset = (height) => {
    let offset = 0;

    candidates().forEach((element) => {
      if (!isVisibleBottomBar(element, height)) return;
      const rect = element.getBoundingClientRect();
      offset = Math.max(offset, Math.ceil(height - Math.max(0, rect.top)));
    });

    /*
     * Em alguns previews o iframe da barra pertence ao documento pai e não é
     * consultável pelo storefront. Nesse caso, o papel UNPUBLISHED é um sinal
     * confiável e a barra padrão do Shopify ocupa aproximadamente 60px.
     */
    const role = String(window.Shopify?.theme?.role || '').toLowerCase();
    const isPreviewTheme = role && role !== 'main' && role !== 'live';
    if (offset === 0 && isPreviewTheme) offset = 60;

    return Math.min(offset, Math.floor(height * 0.25));
  };

  const observeCandidateSizes = () => {
    if (typeof ResizeObserver !== 'function') return;
    resizeObserver ||= new ResizeObserver(() => schedule());
    resizeObserver.disconnect();
    candidates().forEach((element) => resizeObserver.observe(element));
  };

  const measure = () => {
    frameId = 0;
    const height = viewportHeight();
    const offset = detectOffset(height);
    const availableHeight = Math.max(240, height - offset);

    state = { offset, viewportHeight: height, availableHeight };

    document.documentElement.style.setProperty(
      '--saibai-shopify-preview-offset',
      `${offset}px`
    );
    document.documentElement.style.setProperty(
      '--saibai-available-viewport-height',
      `${availableHeight}px`
    );

    observeCandidateSizes();
    subscribers.forEach((callback) => callback(state));
    return state;
  };

  const schedule = () => {
    if (frameId) cancelAnimationFrame(frameId);
    frameId = requestAnimationFrame(measure);
  };

  const containsPreviewBar = (node) => {
    if (!(node instanceof Element)) return false;
    return node.matches(selector) || Boolean(node.querySelector(selector));
  };

  const start = () => {
    if (started) return;
    started = true;

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });

    mutationObserver = new MutationObserver((mutations) => {
      const changed = mutations.some((mutation) => {
        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
        return nodes.some(containsPreviewBar);
      });
      if (changed) schedule();
    });

    mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    measure();
  };

  return {
    subscribe(callback) {
      start();
      subscribers.add(callback);
      callback(state.viewportHeight ? state : measure());
      return () => subscribers.delete(callback);
    },
    refresh() {
      start();
      return measure();
    }
  };
})();

class ModalComponent extends HTMLElement {
  constructor() {
    super();

    this._documentClick = (event) => {
      const toggle = event.target instanceof Element
        ? event.target.closest(`[data-drawer-toggle="#${this.id}"]`)
        : null;
      if (!toggle) return;

      event.preventDefault();
      this.isOpen ? this.close() : this.show(toggle);
    };

    this._onKeyup = (event) => {
      if (event.code === 'Escape') this.close();
    };

    this._onOverlayClick = () => this.close();
  }

  connectedCallback() {
    if (this.parentNode !== document.body) {
      this.dataset.moved = 'true';
      document.body.appendChild(this);
      return;
    }

    this.dataset.moved = 'true';

    Object.assign(this.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100dvh',
      zIndex: '12100'
    });

    if (this.dataset.modalComponentBound === 'true') {
      this.applyViewportMetrics(SaibaiPreviewViewport.refresh());
      return;
    }

    this.dataset.modalComponentBound = 'true';
    document.addEventListener('click', this._documentClick);
    this.addEventListener('keyup', this._onKeyup);

    const overlay = this.querySelector('#Modal-Overlay, [data-modal-overlay]');
    if (overlay && overlay.dataset.modalOverlayBound !== 'true') {
      overlay.dataset.modalOverlayBound = 'true';
      overlay.addEventListener('click', this._onOverlayClick);
    }

    this._unsubscribeViewport = SaibaiPreviewViewport.subscribe((metrics) => {
      this.applyViewportMetrics(metrics);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._documentClick);
    this.removeEventListener('keyup', this._onKeyup);
    this.querySelector('#Modal-Overlay, [data-modal-overlay]')
      ?.removeEventListener('click', this._onOverlayClick);
    this._unsubscribeViewport?.();
    delete this.dataset.modalComponentBound;
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  applyViewportMetrics(metrics) {
    const inner = this.querySelector('.modal__inner');
    if (!inner || !metrics) return;

    const offset = Math.max(0, Number(metrics.offset) || 0);
    const availableHeight = Math.max(240, Number(metrics.availableHeight) || 240);

    this.style.setProperty('--saibai-cart-modal-bottom-offset', `${offset}px`);
    inner.style.setProperty('bottom', `${offset}px`, 'important');
    inner.style.setProperty(
      'max-height',
      `min(var(--popup-max-height, 70dvh), ${availableHeight}px)`,
      'important'
    );
  }

  show(opener) {
    document
      .querySelectorAll('modal-component[data-modal-content][open]')
      .forEach((modal) => {
        if (modal !== this && typeof modal.close === 'function') modal.close();
      });

    this.openedBy = opener;
    this.applyViewportMetrics(SaibaiPreviewViewport.refresh());
    this.animate(true);
  }

  close() {
    if (!this.isOpen) return;
    this.animate(false);
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
  }

  async animate(open) {
    const inner = this.querySelector('.modal__inner');
    if (!inner) return;

    const sequence = (this._animationSequence || 0) + 1;
    this._animationSequence = sequence;

    if (open) this.setAttribute('open', '');

    const motion = typeof Motion !== 'undefined' ? Motion : window.Motion;
    if (motion?.animate) {
      const animation = motion.animate(
        inner,
        {
          opacity: open ? [0, 1] : [1, 0],
          transform: open
            ? ['translateY(2rem)', 'translateY(0)']
            : ['translateY(0)', 'translateY(2rem)']
        },
        {
          duration: open ? 0.35 : 0.25,
          easing: [0.7, 0, 0.3, 1]
        }
      );

      try {
        await animation.finished;
      } catch (_) {}
    }

    if (sequence !== this._animationSequence) return;

    if (!open) {
      this.removeAttribute('open');
      return;
    }

    if (typeof trapFocus === 'function') {
      trapFocus(inner, this.querySelector('.modal__close') || inner);
    }
  }
}

if (!customElements.get('modal-component')) {
  customElements.define('modal-component', ModalComponent);
}
